import useProviderType from '@app/src/hooks/useProviderType'
import type { AutomationFormData } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import { AUTOMATION_QUESTION_KEYS } from '@app/src/pages/Configurations/ConfigurationsPages/Automation/CreateAutomationModal'
import { BoltOutlined } from '@mui/icons-material'
import {
  Alert,
  Box,
  Card,
  CardActionArea,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useIntl } from 'react-intl'

enum ApplyToOption {
  FUTURE_ONLY = 'futureOnly',
  EXISTING_AND_FUTURE = 'existingAndFuture',
}

export interface CreditInfo {
  remaining: number
  required: number
}

interface ApplyToSectionProps {
  bannerMessageId?: string
  affectedCount: number | undefined
  isLoadingCount: boolean
  creditInfo?: CreditInfo
  isEditing?: boolean
  futureOnlyDescriptionId?: string
  existingAndFutureDescriptionId?: string
  messageValues?: Record<string, string>
}

const ApplyToSection: React.FC<ApplyToSectionProps> = ({
  bannerMessageId,
  affectedCount,
  isLoadingCount,
  creditInfo,
  isEditing,
  futureOnlyDescriptionId = 'form.createAutomation.applyTo.futureOnlyDescription',
  existingAndFutureDescriptionId = 'form.createAutomation.applyTo.existingAndFutureDescription',
  messageValues,
}) => {
  const { formatMessage } = useIntl()
  const providerType = useProviderType()
  const { setValue, watch, formState, clearErrors } = useFormContext<AutomationFormData>()
  const applyToExisting = watch(AUTOMATION_QUESTION_KEYS.APPLY_TO_EXISTING)
  const applyToError = formState.errors[AUTOMATION_QUESTION_KEYS.APPLY_TO_EXISTING]

  const insufficientCredits = creditInfo !== undefined && creditInfo.remaining < creditInfo.required
  const noEligibleCompanies = !isLoadingCount && affectedCount !== undefined && affectedCount === 0
  const existingDisabled = isEditing || insufficientCredits || noEligibleCompanies

  const handleApplyToChange = (value: boolean) => {
    if (value && existingDisabled) return
    setValue(AUTOMATION_QUESTION_KEYS.APPLY_TO_EXISTING, value)
    if (applyToError) {
      clearErrors(AUTOMATION_QUESTION_KEYS.APPLY_TO_EXISTING)
    }
  }

  const tooltipTitle = isEditing
    ? formatMessage({ id: 'form.createAutomation.applyTo.cannotApplyToExistingWhenEditing' })
    : noEligibleCompanies
      ? formatMessage({ id: 'form.createAutomation.applyTo.noEligibleCompanies' })
      : insufficientCredits && creditInfo
        ? formatMessage(
            { id: 'form.createAutomation.applyTo.insufficientCredits' },
            { remaining: creditInfo.remaining, required: creditInfo.required },
          )
        : ''

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1" fontWeight={600}>
        {formatMessage({ id: 'form.createAutomation.applyTo.title' })}
      </Typography>

      {applyToError && (
        <Alert severity="error">{formatMessage({ id: 'form.createAutomation.applyTo.selectionRequired' })}</Alert>
      )}

      <RadioGroup
        value={
          applyToExisting === true
            ? ApplyToOption.EXISTING_AND_FUTURE
            : applyToExisting === false
              ? ApplyToOption.FUTURE_ONLY
              : ''
        }
        onChange={e => handleApplyToChange(e.target.value === ApplyToOption.EXISTING_AND_FUTURE)}
      >
        <Stack spacing={2}>
          <Card
            elevation={0}
            variant="outlined"
            sx={{
              borderColor: applyToExisting === false ? 'primary.main' : 'divider',
              borderWidth: applyToExisting === false ? 2 : 1,
            }}
          >
            <CardActionArea onClick={() => handleApplyToChange(false)} sx={{ p: 2 }}>
              <FormControlLabel
                value={ApplyToOption.FUTURE_ONLY}
                control={<Radio checked={applyToExisting === false} />}
                label={
                  <Stack pl={2}>
                    <Typography variant="subtitle1">
                      {formatMessage({ id: 'form.createAutomation.applyTo.futureOnly' })}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {formatMessage({ id: futureOnlyDescriptionId }, messageValues)}
                    </Typography>
                  </Stack>
                }
                sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
              />
            </CardActionArea>
          </Card>

          <Tooltip title={tooltipTitle} arrow disableHoverListener={!existingDisabled}>
            <Card
              elevation={0}
              variant="outlined"
              sx={{
                borderColor:
                  applyToExisting === true ? 'primary.main' : existingDisabled ? 'action.disabled' : 'divider',
                borderWidth: applyToExisting === true ? 2 : 1,
                opacity: existingDisabled ? 0.6 : 1,
              }}
            >
              <CardActionArea onClick={() => handleApplyToChange(true)} disabled={existingDisabled} sx={{ p: 2 }}>
                <FormControlLabel
                  value={ApplyToOption.EXISTING_AND_FUTURE}
                  control={<Radio checked={applyToExisting === true} disabled={existingDisabled} />}
                  label={
                    <Stack pl={2}>
                      <Typography variant="subtitle1">
                        {formatMessage({ id: 'form.createAutomation.applyTo.existingAndFuture' })}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {formatMessage({ id: existingAndFutureDescriptionId }, { ...messageValues, providerType })}
                      </Typography>
                    </Stack>
                  }
                  sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                />
              </CardActionArea>
              {insufficientCredits && creditInfo && (
                <Box sx={{ px: 2, pb: 2, pt: 0 }}>
                  <Alert severity="error">
                    {formatMessage(
                      { id: 'form.createAutomation.applyTo.insufficientCredits' },
                      { remaining: creditInfo.remaining, required: creditInfo.required },
                    )}
                  </Alert>
                </Box>
              )}
            </Card>
          </Tooltip>
        </Stack>
      </RadioGroup>

      {creditInfo !== undefined && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {formatMessage(
              { id: 'form.createAutomation.applyTo.screeningCreditsAvailable' },
              { count: creditInfo.remaining },
            )}
          </Typography>
        </Box>
      )}

      {applyToExisting === true && bannerMessageId && (
        <Box
          sx={{
            bgcolor: 'info.main',
            p: 2,
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <BoltOutlined />
          <Typography>
            {isLoadingCount ? (
              <Skeleton width={200} sx={{ display: 'inline-block' }} />
            ) : (
              formatMessage({ id: bannerMessageId }, { ...messageValues, count: affectedCount ?? 0 })
            )}
          </Typography>
        </Box>
      )}
    </Stack>
  )
}

export default ApplyToSection
